/**
 * #979 — the page's one "Add New Source" dialog.
 *
 * Contract §1: creating a name adds it to the list WITHOUT selecting it;
 * Enter submits once; Escape/Cancel creates nothing; duplicates are reused,
 * never added twice. Rendered through `atoms/Modal`, whose `data-modal-root`
 * + `aria-modal` the LT2 listener already treats as page-wide hotkey
 * suppression, and whose input the listener treats as typing.
 *
 * The draft lives here, not in the controller: it belongs to one open dialog
 * and nothing else needs it. The provider keys this component per opening,
 * so a reopened dialog always starts empty.
 */
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useStore } from 'zustand';
import { Modal } from '@/components/atoms/Modal';
import type { CardSourcesController, CreateOutcome } from '@/lib/cardSources/cardSourcesController';
import {
  SOURCE_NAME_MAX_CODE_POINTS,
  checkSourceName,
  codePointLength,
  findDuplicate,
} from '@/lib/cardSources/sourceName';
import { knownSources } from '@/lib/cardSources/cardSourcesController';

type Failure = Extract<CreateOutcome, { ok: false }>['reason'];

const FAILURE_TEXT: Partial<Record<Failure, string>> = {
  failed: 'Couldn’t add the source. Your text is kept — try again.',
  invalid_name: 'That name isn’t allowed.',
  busy: 'An earlier add is still waiting for an answer — try again in a moment.',
};

export function AddSourceDialog({
  controller,
  onClose,
}: {
  controller: CardSourcesController;
  /** Called ONCE: `added` is the new or reused source, null if nothing was added. */
  onClose: (added: { name: string; reused: boolean } | null) => void;
}) {
  const ids = useId();
  const [draft, setDraft] = useState('');
  const [failure, setFailure] = useState<Failure | null>(null);
  // This dialog's own submit. An earlier dialog's create may still be in
  // flight; the controller refuses a second one as `busy`, shown below.
  const [submitting, setSubmitting] = useState(false);
  const closed = useRef(false);
  // Hidden by an account switch = closed: a late answer must not report back.
  useEffect(() => {
    // The app replays effect setup/cleanup in development StrictMode.
    // Only an actual cleanup should leave this dialog closed.
    closed.current = false;
    return () => {
      closed.current = true;
    };
  }, []);
  const known = useStore(controller.store, (s) => knownSources(s.dictionary));

  // Once closed — by Cancel/Escape/X/backdrop while a create was in flight, or
  // by an account switch — a late answer must not reopen a menu or move focus.
  const finish = (added: { name: string; reused: boolean } | null) => {
    if (closed.current) return;
    closed.current = true;
    onClose(added);
  };

  const check = checkSourceName(draft);
  const duplicate = check.ok ? findDuplicate(known ?? [], check.name) : undefined;
  const length = codePointLength(draft.trim());
  const canAdd = check.ok && !duplicate && !submitting;

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!canAdd) return;
    setFailure(null);
    setSubmitting(true);
    const outcome = await controller.createSource(draft);
    if (closed.current) return;
    setSubmitting(false);
    if (outcome.ok) finish({ name: outcome.source.name, reused: outcome.reused });
    else if (outcome.reason === 'stale') finish(null);
    else setFailure(outcome.reason);
  };

  // Always closable, even while waiting: a create that never answers must not
  // hold a modal (and so page-wide hotkey suppression) open. A name already
  // sent may still land and appear in the list; closing only stops waiting.
  const cancel = () => finish(null);

  const message = duplicate
    ? `“${duplicate.name}” is already in your list.`
    : check.ok || draft.trim() === ''
      ? failure
        ? FAILURE_TEXT[failure]
        : null
      : `Names can be at most ${SOURCE_NAME_MAX_CODE_POINTS} characters.`;

  return (
    <Modal
      isOpen
      onClose={cancel}
      title="Add New Source"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={cancel}
            className="bg-muted hover:bg-muted/80 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form={`${ids}-form`}
            disabled={!canAdd}
            className="rounded-lg bg-purple-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-purple-500/90 disabled:opacity-50"
          >
            {submitting ? 'Adding…' : 'Add'}
          </button>
        </div>
      }
    >
      <form id={`${ids}-form`} onSubmit={(e) => void submit(e)} noValidate>
        <label className="text-muted-foreground mb-1 block text-xs" htmlFor={`${ids}-name`}>
          Source name
        </label>
        <input
          id={`${ids}-name`}
          type="text"
          // Focus the name field on open, as the mockup shows.
          autoFocus
          autoComplete="off"
          spellCheck={false}
          value={draft}
          readOnly={submitting}
          aria-invalid={message != null && !failure}
          aria-describedby={`${ids}-help`}
          onChange={(e) => {
            setDraft(e.target.value);
            setFailure(null);
          }}
          className="bg-background border-border focus:border-purple-400 w-full rounded-lg border px-3 py-2 text-sm outline-none"
        />
        <div id={`${ids}-help`} className="mt-1 flex items-start justify-between gap-3 text-xs">
          {/* A long unbroken name must wrap inside the dialog, not run past it. */}
          <span
            role={message ? 'alert' : undefined}
            className={`min-w-0 [overflow-wrap:anywhere] ${message ? 'text-amber-300 light:text-amber-700' : 'text-muted-foreground'}`}
          >
            {message ?? 'Adds the name to your list. Choose it on any card afterwards.'}
          </span>
          <span
            className={`shrink-0 tabular-nums ${length > SOURCE_NAME_MAX_CODE_POINTS ? 'text-amber-300 light:text-amber-700' : 'text-muted-foreground'}`}
          >
            {length}/{SOURCE_NAME_MAX_CODE_POINTS}
          </span>
        </div>
      </form>
    </Modal>
  );
}
